import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { scenarioService } from '../../services/api';

const ScenariosStep = ({ project, drivers, scenarios, onDataChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const canGenerateScenarios = drivers.length > 0 && drivers.every(d => d.extreme_value_low && d.extreme_value_high);
  const possibleScenarios = Math.pow(2, drivers.length);
  const selectedCount = scenarios.filter(s => s.is_selected).length;

  // Sort scenarios by ID to maintain static order
  const sortedScenarios = [...scenarios].sort((a, b) => {
    if (a.id && b.id) return a.id - b.id;
    return 0;
  });

  // `getTranslatedCategory` servia per posar la categoria del driver sota el
  // seu nom a la capçalera de la taula. Sense taula no hi ha capçalera, i dins
  // del bloc d'un escenari la categoria no aporta res: el que importa allà és
  // quin extrem ha pres, no de quina família PESTEL surt el driver.

  const handleGenerateScenarios = async () => {
    setLoading(true);
    try {
      if (project.workspace_id) {
        await scenarioService.generateCaseScenarios(project.id);
      } else {
        await scenarioService.generateScenarios(project.id);
      }
      toast.success(t('scenarios.generateSuccess'));
      onDataChange();
    } catch (error) {
      toast.error(t('scenarios.generateError'));
      console.error('Error generating scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelection = async (scenarioId) => {
    try {
      await scenarioService.toggleScenarioSelection(scenarioId);
      onDataChange();
    } catch (error) {
      toast.error(t('scenarios.updateSelectionError'));
      console.error('Error updating scenario selection:', error);
    }
  };

  // Escriure el nom d'un escenari
  //
  // Abans, cada tecla feia un PUT i tot seguit recarregava la llista sencera,
  // i el valor de la caixa sortia directament del servidor. Com que les
  // respostes tornaven desordenades i sempre més tard que el teclejat, el que
  // acabaves d'escriure se sobreescrivia amb el que havia desat una petició
  // anterior: es perdien lletres i n'apareixien de canviades.
  //
  // Ara el que mana mentre escrius és l'esborrany local. Es desa 700 ms després
  // de l'última tecla, o de seguida que surts de la caixa. L'esborrany no
  // s'esborra en desar: si ho fes, la recàrrega —que triga— faria parpellejar
  // el nom vell durant un instant.
  const [nomsEsborrany, setNomsEsborrany] = useState({});
  const temporitzadors = useRef({});

  const desaNom = useCallback(async (scenarioId, name) => {
    try {
      await scenarioService.updateScenario(scenarioId, { name });
      // Una sola recàrrega, quan la cosa ja s'ha aturat: la passa 6 ensenya
      // els noms dels escenaris i s'ha d'assabentar del canvi.
      onDataChange();
    } catch (error) {
      toast.error(t('scenarios.updateNameError'));
      console.error('Error updating scenario name:', error);
    }
  }, [onDataChange, t]);

  const handleNameChange = (scenarioId, name) => {
    setNomsEsborrany(prev => ({ ...prev, [scenarioId]: name }));
    clearTimeout(temporitzadors.current[scenarioId]);
    temporitzadors.current[scenarioId] = setTimeout(() => desaNom(scenarioId, name), 700);
  };

  const handleNameBlur = (scenarioId) => {
    const pendent = nomsEsborrany[scenarioId];
    if (pendent === undefined) return;
    clearTimeout(temporitzadors.current[scenarioId]);
    desaNom(scenarioId, pendent);
  };

  // Si el component desapareix amb un desat pendent, es desa igualment.
  useEffect(() => {
    const timers = temporitzadors.current;
    const esborranys = nomsEsborrany;
    return () => {
      Object.entries(timers).forEach(([id, timer]) => {
        if (!timer) return;
        clearTimeout(timer);
        if (esborranys[id] !== undefined) {
          scenarioService.updateScenario(id, { name: esborranys[id] }).catch(() => {});
        }
      });
    };
  }, [nomsEsborrany]);

  // Abans això deia «Tots els valors alts», «Tots els valors baixos» o
  // «Mixt (2A/1B)». Comptar quants drivers estan «alts» només té sentit si els
  // extrems són magnituds, i no ho són: són dos estats del món. Un escenari on
  // tots els drivers han pres el seu extrem A no és «tot baix», és una
  // combinació com qualsevol altra. El nom de debò el posa qui hi treballa.
  const getDefaultScenarioName = (scenario, index) => t('scenarios.defaultName', { index: index + 1 });

  return (
    <div className="space-y-6">
      {!canGenerateScenarios ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            {t('scenarios.cannotGenerateYet')}
          </h3>
          <p className="text-neutral-600">
            {t('factors.extremeValues.instructionsText', { timeHorizon: project.time_horizon || t('common.notSpecified') })}
          </p>
        </div>
      ) : (
        <>
          {/* Compact Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-neutral-900">{t('scenarios.title')}</h3>
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
                  title={t('scenarios.showHelp')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-neutral-600">
                  {sortedScenarios.length > 0 
                    ? t('scenarios.scenariosGenerated', { count: sortedScenarios.length, total: possibleScenarios })
                    : t('scenarios.possibleCombinations', { count: possibleScenarios, drivers: drivers.length })
                  }
                </span>
                {selectedCount > 0 && (
                  <div className="text-xs text-neutral-500 bg-accent-100 px-2 py-1 rounded">
                    {t('scenarios.selectedCount', { count: selectedCount })}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {sortedScenarios.length === 0 ? (
                <button
                  onClick={handleGenerateScenarios}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  )}
                  {loading ? t('scenarios.generating') : t('scenarios.generate')}
                </button>
              ) : (
                <button
                  onClick={handleGenerateScenarios}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? t('scenarios.regenerating') : t('scenarios.regenerate')}
                </button>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              Un bloc per escenari (redisseny de l'11/08/2026).

              Abans era una taula amb una columna per driver, i cada cel·la
              tenia **128 píxels** amb `truncate`. Des que els extrems s'escriuen
              com a estats del món —«Regulació feble i fragmentada, cada estat
              membre pel seu compte»— gairebé cap hi cabia: es veien tres punts
              i es triaven escenaris sense saber què hi havia al darrere.

              Amb blocs no hi ha problema d'amplada **mai**: els valors es
              reparteixen en les columnes que hi càpiguen, i amb cinc drivers es
              reparteixen en més files. El text hi va sencer.

              El que es perd és la lectura en columna —comparar què fa el mateix
              driver a tots els escenaris obliga a recórrer els blocs—, i era
              una decisió conscient.
              ══════════════════════════════════════════════════════════════ */}
          {sortedScenarios.length > 0 && (
            <div className="space-y-3">
              {/* Triar-los tots o cap, que abans era la casella de la capçalera
                  de la taula i sense taula no tindria on viure. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-600">
                  <input
                    type="checkbox"
                    checked={selectedCount === sortedScenarios.length && selectedCount > 0}
                    onChange={(e) => {
                      sortedScenarios.forEach(scenario => {
                        if (scenario.is_selected !== e.target.checked) {
                          handleToggleSelection(scenario.id);
                        }
                      });
                    }}
                    className="h-4 w-4 rounded border-neutral-300 text-accent-600 focus:ring-accent-500"
                  />
                  {t('scenarios.selectAll')}
                </label>
                <span className="font-mono text-[11px] text-neutral-500">
                  {t('scenarios.selectedFromTotal', {
                    selected: selectedCount, total: sortedScenarios.length
                  })}
                </span>
              </div>

              {sortedScenarios.map((scenario, index) => (
                <div
                  key={scenario.id}
                  className={`rounded-lg border p-4 transition-colors ${
                    scenario.is_selected
                      ? 'border-accent-300 bg-accent-50/40'
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                  }`}
                >
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <input
                      type="checkbox"
                      checked={scenario.is_selected}
                      onChange={() => handleToggleSelection(scenario.id)}
                      aria-label={t('scenarios.selectScenario')}
                      className="h-4 w-4 flex-shrink-0 rounded border-neutral-300 text-accent-600 focus:ring-accent-500"
                    />
                    <input
                      type="text"
                      value={nomsEsborrany[scenario.id] ?? scenario.name ?? ''}
                      onChange={(e) => handleNameChange(scenario.id, e.target.value)}
                      onBlur={() => handleNameBlur(scenario.id)}
                      placeholder={getDefaultScenarioName(scenario, index)}
                      className="min-w-[12rem] flex-1 rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-sm font-medium text-neutral-900 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 sm:max-w-sm"
                    />
                    <span className="ml-auto font-mono text-[11px] text-neutral-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                    {drivers.map(driver => {
                      const value = scenario.driver_values?.[driver.id.toString()];
                      const esB = value === driver.extreme_value_high;
                      return (
                        <div key={driver.id}>
                          <p className="font-mono text-[9.5px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                            {driver.causal_factor_name}
                          </p>
                          <p className="mt-1 flex items-start gap-2 text-[13px] leading-relaxed text-neutral-700">
                            <span className="mt-0.5 flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-[3px] border border-neutral-300 font-mono text-[9.5px] font-semibold text-neutral-500">
                              {esB ? t('scenarios.high') : t('scenarios.low')}
                            </span>
                            {/* Sense retall: el text hi va sencer, que és tot
                                el motiu d'aquest canvi. */}
                            <span>{value}</span>
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Help Modal */}
          {showHelpModal && (
            <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl">
                <div className="p-6 border-b border-neutral-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-neutral-900">
                      {t('scenarios.helpTitle')}
                    </h3>
                    <button
                      onClick={() => setShowHelpModal(false)}
                      className="text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="prose prose-sm max-w-none text-neutral-700">
                    <p className="mb-4">
                      {t('scenarios.stepIntroduction', { count: possibleScenarios, drivers: drivers.length })}
                    </p>
                    
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('scenarios.howItWorks')}:</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('scenarios.combinationsExplanation')}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('scenarios.selectionExplanation')}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('scenarios.namingExplanation')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScenariosStep;